import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../components/AdminHeader';
import { getAnalytics, exportToCSV, Analytics } from '../services/analytics';
import './AdminAnalyticsPage.css';

const AdminAnalyticsPage: React.FC = () => {
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedDealerId, setSelectedDealerId] = useState<string>('');
  const navigate = useNavigate();

  useEffect(() => {
    loadAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (selectedDealerId) params.dealerId = selectedDealerId;

      const data = await getAnalytics(params);
      setAnalytics(data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    loadAnalytics();
  };

  const handleReset = () => {
    setStartDate('');
    setEndDate('');
    setSelectedDealerId('');
    loadAnalytics();
  };

  const handleExport = () => {
    if (analytics) {
      exportToCSV(analytics);
    }
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Alle';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE');
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  return (
    <div>
      <AdminHeader />
      <div className="container">
        <div className="card">
          <h2>Filter</h2>
          <div className="filter-controls">
            <div className="form-group">
              <label htmlFor="startDate">Von Datum:</label>
              <input
                id="startDate"
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="endDate">Bis Datum:</label>
              <input
                id="endDate"
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="dealer">Händler (optional):</label>
              <select
                id="dealer"
                value={selectedDealerId}
                onChange={e => setSelectedDealerId(e.target.value)}
              >
                <option value="">Alle Händler</option>
                {analytics?.dealers.map(dealer => (
                  <option key={dealer.id} value={dealer.id}>
                    {dealer.username}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <button onClick={handleFilter} className="btn btn-primary">
                Filtern
              </button>
              <button
                onClick={handleReset}
                className="btn btn-secondary"
                style={{ marginLeft: '10px' }}
              >
                Zurücksetzen
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="loading">Lade Auswertungen...</div>
        ) : analytics ? (
          <>
            {/* Section 1: Dealer/Model/Timeframe */}
            <div className="card">
              <div className="card-header">
                <div>
                  <h2>Händler / Modell / Zeitraum</h2>
                  <p className="timeframe-info">
                    Zeitraum: {formatDate(analytics.timeRange.startDate)} -{' '}
                    {formatDate(analytics.timeRange.endDate)}
                  </p>
                </div>
                <button onClick={handleExport} className="btn btn-primary">
                  Als CSV exportieren
                </button>
              </div>
              {analytics.dealerModelRelation.length === 0 ? (
                <p className="no-data">Keine Daten im ausgewählten Zeitraum.</p>
              ) : (
                <div className="table-container">
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>Händler</th>
                        <th>Modell</th>
                        <th>Anzahl</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.dealerModelRelation.map((item, index) => (
                        <tr key={`${item.dealerId}-${item.modelId}-${index}`}>
                          <td>{item.dealerName}</td>
                          <td>{item.modelName}</td>
                          <td className="text-right">{item.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Section 2: Dealer/Revenue/Timeframe */}
            <div className="card">
              <div className="card-header">
                <div>
                  <h2>Händler / Umsatz / Zeitraum</h2>
                  <p className="timeframe-info">
                    Zeitraum: {formatDate(analytics.timeRange.startDate)} -{' '}
                    {formatDate(analytics.timeRange.endDate)}
                  </p>
                </div>
              </div>
              {analytics.dealerRevenue.length === 0 ? (
                <p className="no-data">Keine Daten im ausgewählten Zeitraum.</p>
              ) : (
                <div className="table-container">
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>Händler</th>
                        <th>Umsatz</th>
                        <th>Anzahl Bestellungen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.dealerRevenue.map(item => (
                        <tr key={item.dealerId}>
                          <td>{item.dealerName}</td>
                          <td className="text-right revenue">{formatCurrency(item.revenue)}</td>
                          <td className="text-right">{item.orderCount}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="total-row">
                        <td>
                          <strong>Gesamt</strong>
                        </td>
                        <td className="text-right revenue">
                          <strong>
                            {formatCurrency(
                              analytics.dealerRevenue.reduce((sum, item) => sum + item.revenue, 0)
                            )}
                          </strong>
                        </td>
                        <td className="text-right">
                          <strong>
                            {analytics.dealerRevenue.reduce(
                              (sum, item) => sum + item.orderCount,
                              0
                            )}
                          </strong>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="error">Fehler beim Laden der Auswertungen</div>
        )}
      </div>
    </div>
  );
};

export default AdminAnalyticsPage;
